import { useState, useEffect, useCallback, useRef } from 'react';
import { connectionsService } from '../../api/connectionsService';
import type { IngestionStatusResponse } from '../../api/types';

interface UseIngestionProgressOptions {
  /** Polling interval while actively syncing (ms) */
  activeInterval?: number;
  /** Polling interval when idle/done (ms) */
  idleInterval?: number;
  /** Whether polling is enabled */
  enabled?: boolean;
}

interface UseIngestionProgressResult {
  status: IngestionStatusResponse | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook that polls GET /connections/:id/ingestion-status with adaptive interval.
 *
 * - 1s while status is "syncing" (active ingestion)
 * - 15s when idle/done/error
 * - Stops polling after 5 consecutive terminal checks
 */
export function useIngestionProgress(
  connectionId: string | null | undefined,
  options: UseIngestionProgressOptions = {}
): UseIngestionProgressResult {
  const {
    activeInterval = 2000,
    idleInterval = 15000,
    enabled = true,
  } = options;

  const [status, setStatus] = useState<IngestionStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store intervals in refs so changing them doesn't restart the polling effect.
  // The effect reads the latest values each cycle without needing them as deps.
  const activeIntervalRef = useRef(activeInterval);
  const idleIntervalRef = useRef(idleInterval);
  activeIntervalRef.current = activeInterval;
  idleIntervalRef.current = idleInterval;

  // Use refs to avoid stale closures and prevent re-render loops
  const statusRef = useRef<IngestionStatusResponse | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const doneCountRef = useRef(0);
  const errorCountRef = useRef(0);
  const isInitialFetchRef = useRef(true);

  const fetchStatus = useCallback(async () => {
    if (!connectionId || !mountedRef.current) return;

    try {
      // Only show loading spinner on initial fetch, not on every poll tick
      if (isInitialFetchRef.current) setIsLoading(true);

      const result = await connectionsService.getIngestionStatus(connectionId);
      if (!mountedRef.current) return;

      statusRef.current = result;
      setStatus(result);
      setError(null);
      errorCountRef.current = 0; // Reset error streak on success

      // Track consecutive terminal polls to stop polling
      if (result.status === 'ready' || result.status === 'error' || result.status === 'reauth_required') {
        doneCountRef.current += 1;
      } else {
        doneCountRef.current = 0;
      }
    } catch (err) {
      if (!mountedRef.current) return;
      errorCountRef.current += 1;
      setError(err instanceof Error ? err.message : 'Failed to fetch ingestion status');
    } finally {
      if (mountedRef.current) {
        isInitialFetchRef.current = false;
        setIsLoading(false);
      }
    }
  }, [connectionId]);

  // Extract scheduleNextPoll into a ref so refetch can restart the loop
  const scheduleNextPollRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    mountedRef.current = true;

    if (!connectionId || !enabled) return;

    // Schedule next poll AFTER fetch completes, using latest status
    const scheduleNextPoll = () => {
      if (!mountedRef.current) return;

      // Stop polling after 5 consecutive terminal checks
      if (doneCountRef.current >= 5) return;

      // Read current status from ref (always up-to-date after fetch)
      const isSyncing = statusRef.current?.status === 'syncing';
      // Exponential backoff on consecutive errors: base × 2^(errors-1), capped at 30s
      const errorBackoff = errorCountRef.current > 0
        ? Math.min(activeIntervalRef.current * Math.pow(2, errorCountRef.current), 30_000)
        : 0;
      const interval = errorBackoff || (isSyncing ? activeIntervalRef.current : idleIntervalRef.current);

      timerRef.current = setTimeout(async () => {
        await fetchStatus();
        if (mountedRef.current) {
          scheduleNextPoll();
        }
      }, interval);
    };

    scheduleNextPollRef.current = scheduleNextPoll;

    // Initial fetch — AWAIT it, then start scheduling
    (async () => {
      await fetchStatus();
      if (mountedRef.current) {
        scheduleNextPoll();
      }
    })();

    return () => {
      mountedRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  // Note: activeInterval/idleInterval intentionally excluded — read from refs
  // so that changing intervals doesn't restart the polling loop.
  }, [connectionId, enabled, fetchStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    // Reset counters so polling resumes
    doneCountRef.current = 0;
    errorCountRef.current = 0;

    // Cancel any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Fetch immediately, then restart the polling loop
    (async () => {
      await fetchStatus();
      if (mountedRef.current && scheduleNextPollRef.current) {
        scheduleNextPollRef.current();
      }
    })();
  }, [fetchStatus]);

  return { status, isLoading, error, refetch };
}